/**
 * The Fabric clusters this Studio environment runs organizations on, from the registry checked in
 * at backend/shared/src/utils/studioFabricRegistry.ts, and the organizations placed on each.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { adminClient, type AdminFabricRow, type AdminOrgFabricPlacement } from '@/api/adminClient';
import { StatusPill } from '@/components/runs/StatusPill';
import { AdminPageHeader, PAGE_CONTAINER_CLASS } from '@/components/shell/AdminPageHeader';
import { AdminSection } from '@/components/shell/AdminSection';
import { DataTable, DataTableCell, DataTableHead, DataTableLoadError, DataTableMessage, DataTableRow } from '@/components/table/DataTable';
import { orgBasePath } from '@/lib/orgNav';
import { FabricCapacitySection } from '@/pages/FabricCapacitySection';
import { FABRIC_STATE_LABELS, FABRIC_STATE_TONES } from '@/pages/org/OrgFabricPage';

const FABRIC_COLUMNS = [{ label: 'Cluster' }, { label: 'Region' }, { label: 'Platform API' }, { label: 'Organizations' }, { label: '' }];
const ORG_COLUMNS = [{ label: 'Organization' }, { label: 'Namespace' }, { label: 'State' }];

function total(row: AdminFabricRow): number {
  return Object.values(row.organizations).reduce((sum, count) => sum + count, 0);
}

function FabricOrganizations({ fabric }: { fabric: AdminFabricRow }) {
  const [items, setItems] = useState<AdminOrgFabricPlacement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    adminClient
      .listFabricOrganizations(fabric.id)
      .then((result) => {
        if (!cancelled) setItems(result.items);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load organizations');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fabric.id, reloadKey]);

  return (
    <AdminSection title={`Organizations on ${fabric.displayName}`} description={fabric.id} bodyClassName="p-0">
      <DataTable>
        <DataTableHead columns={ORG_COLUMNS} />
        {loading ? (
          <DataTableMessage colSpan={ORG_COLUMNS.length} loading>
            Loading organizations
          </DataTableMessage>
        ) : error ? (
          <DataTableLoadError colSpan={ORG_COLUMNS.length} message={error} onRetry={() => setReloadKey((key) => key + 1)} />
        ) : items.length === 0 ? (
          <DataTableMessage colSpan={ORG_COLUMNS.length}>No organizations are placed on this cluster yet</DataTableMessage>
        ) : (
          <tbody>
            {items.map((item) => {
              const to = `${orgBasePath(item.organizationId)}/fabric`;
              return (
                <DataTableRow key={item.organizationId} to={to}>
                  <DataTableCell>
                    <Link to={to} className="font-mono text-xs text-foreground hover:underline">
                      {item.organizationId}
                    </Link>
                  </DataTableCell>
                  <DataTableCell>
                    <span className="font-mono text-xs">{item.namespace}</span>
                  </DataTableCell>
                  <DataTableCell>
                    <StatusPill tone={FABRIC_STATE_TONES[item.state]}>{FABRIC_STATE_LABELS[item.state]}</StatusPill>
                  </DataTableCell>
                </DataTableRow>
              );
            })}
          </tbody>
        )}
      </DataTable>
    </AdminSection>
  );
}

export function FabricsPage() {
  const [fabrics, setFabrics] = useState<AdminFabricRow[]>([]);
  const [environment, setEnvironment] = useState<string>('');
  const [selectedId, setSelectedId] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    adminClient
      .listFabrics()
      .then((result) => {
        if (cancelled) return;
        setFabrics(result.items);
        setEnvironment(result.environment);
        setSelectedId((current) => current ?? result.defaultFabricId ?? result.items[0]?.id);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load Fabrics');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const selected = fabrics.find((fabric) => fabric.id === selectedId);

  return (
    <div className={PAGE_CONTAINER_CLASS}>
      <AdminPageHeader
        title="Fabrics"
        description={`The Fabric clusters ${environment ? `Studio ${environment}` : 'Studio'} places organizations on. Each organization gets its own namespace on one cluster. Clusters are added to the registry in the studio repo.`}
      />

      <div className="space-y-6">
        <DataTable>
          <DataTableHead columns={FABRIC_COLUMNS} />
          {loading ? (
            <DataTableMessage colSpan={FABRIC_COLUMNS.length} loading>
              Loading Fabrics
            </DataTableMessage>
          ) : error ? (
            <DataTableLoadError colSpan={FABRIC_COLUMNS.length} message={error} onRetry={() => setReloadKey((key) => key + 1)} />
          ) : fabrics.length === 0 ? (
            <DataTableMessage colSpan={FABRIC_COLUMNS.length}>No Fabric clusters are registered for this environment</DataTableMessage>
          ) : (
            <tbody>
              {fabrics.map((fabric) => (
                <DataTableRow key={fabric.id} className={fabric.id === selectedId ? 'bg-muted/40' : undefined}>
                  <DataTableCell>
                    <button type="button" onClick={() => setSelectedId(fabric.id)} className="text-left font-medium text-foreground hover:underline">
                      {fabric.displayName}
                    </button>
                    <p className="font-mono text-xs text-muted-foreground">{fabric.id}</p>
                  </DataTableCell>
                  <DataTableCell>
                    <span className="text-sm">{fabric.region}</span>
                    <p className="font-mono text-xs text-muted-foreground">{fabric.account}</p>
                  </DataTableCell>
                  <DataTableCell className="max-w-[20rem]">
                    <span className="block truncate text-xs text-muted-foreground">{fabric.apiUrl}</span>
                  </DataTableCell>
                  <DataTableCell>
                    <span className="text-sm">
                      {fabric.organizations.ready} ready / {total(fabric)}
                    </span>
                    {fabric.organizations.failed ? (
                      <p className="text-xs text-destructive-foreground">{fabric.organizations.failed} failed</p>
                    ) : null}
                  </DataTableCell>
                  <DataTableCell className="space-x-1 text-right">
                    {fabric.default ? <StatusPill tone="success">Default</StatusPill> : null}
                    {fabric.acceptingOrgs ? null : <StatusPill tone="neutral">Closed</StatusPill>}
                  </DataTableCell>
                </DataTableRow>
              ))}
            </tbody>
          )}
        </DataTable>

        <FabricCapacitySection />

        {selected ? <FabricOrganizations key={selected.id} fabric={selected} /> : null}
      </div>
    </div>
  );
}
